import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, CreditCard, MessageSquare, Trash2 } from "lucide-react";
import Pager, { usePager } from "@/components/Pager";

const TYPE_LABELS = {
  topic_selection: "Topic selection",
  literature_review: "Literature review",
  methodology_design: "Methodology design",
  methodology_refinement: "Methodology refinement",
  methodology_implementation: "Methodology implementation",
  statistical_analysis: "Statistical analysis",
  technical_implementation: "Technical implementation",
  publication_preparation: "Publication preparation",
};

const STATUS_BADGES = {
  submitted: { label: "Awaiting price", cls: "border-amber-200 bg-amber-50 text-amber-700" },
  priced: { label: "Ready to pay", cls: "border-sky-200 bg-sky-50 text-sky-700" },
  paid: { label: "Paid · pending mentor", cls: "border-indigo-200 bg-indigo-50 text-indigo-700" },
  assigned: { label: "Mentor assigned", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  in_progress: { label: "In progress", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  completed: { label: "Completed", cls: "border-emerald-300 bg-emerald-100 text-emerald-700" },
};

export default function GuidanceRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const pager = usePager(items, 15);
  const navigate = useNavigate();

  const load = () => api.get("/guidance/mine").then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const deleteRequest = async (g) => {
    const canDelete = g.status === "submitted" || g.status === "priced";
    if (!canDelete) {
      toast.error("You can only delete a request before payment. Ask admin to remove paid/assigned requests.");
      return;
    }
    if (!window.confirm(`Delete this guidance request?\n\n"${(g.problem_description || "").slice(0, 80)}"\n\nThis cannot be undone.`)) return;
    setDeletingId(g.id);
    try {
      await api.delete(`/guidance/${g.id}`);
      toast.success("Request deleted");
      setItems((arr) => arr.filter((x) => x.id !== g.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete the request");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div data-testid="guidance-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">CUSTOM GUIDANCE</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Get help on a <em className="italic text-kb-primary">specific challenge</em>.</h1>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">
            Need help with topic selection, literature review, methodology, stats or publication prep? Submit a request — admin sets the price based on complexity, you pay via Khalti/eSewa, and a mentor takes over.
          </p>
        </div>
        <Link to="/dashboard/guidance/new" className="kb-btn-primary" data-testid="guidance-new-btn">
          <Plus className="w-4 h-4" /> New request
        </Link>
      </div>

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        items.length === 0 ? (
          <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">No guidance requests yet.</div>
        ) : (
          <div className="mt-8 space-y-3">
            {pager.pageItems.map((g) => {
              const badge = STATUS_BADGES[g.status] || { label: g.status, cls: "border-slate-200" };
              return (
                <div key={g.id} className="kb-card p-6" data-testid={`guidance-${g.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">{g.id} · {TYPE_LABELS[g.type] || g.type}</div>
                      <div className="text-kb-ink mt-2 line-clamp-2">{g.problem_description}</div>
                      <div className="kb-mono text-[10px] text-kb-ink-light mt-2">Est. {g.estimated_hours}h · {new Date(g.created_at).toLocaleDateString()}</div>
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      <span className={`kb-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border ${badge.cls}`}>{badge.label}</span>
                      {g.price_npr && <div className="font-mono text-kb-primary">NPR {g.price_npr.toLocaleString()}</div>}
                      {(g.status === "priced" || g.status === "quoted" || g.status === "scheduled") && (
                        g.agreed_slot ? (
                          <button onClick={() => navigate(`/payments/checkout?context_type=guidance&context_id=${g.id}`)}
                            className="kb-btn-primary text-xs" data-testid={`guidance-${g.id}-pay-btn`}>
                            <CreditCard className="w-3.5 h-3.5" /> Pay NPR {g.price_npr?.toLocaleString?.() || g.price_npr}
                          </button>
                        ) : (
                          <Link to={`/dashboard/guidance/${g.id}`} className="kb-mono text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 inline-flex items-center gap-1" data-testid={`guidance-${g.id}-schedule-first`}>
                            Agree on a slot to unlock payment →
                          </Link>
                        )
                      )}
                      {["assigned", "in_progress", "completed"].includes(g.status) && (
                        <Link to={`/dashboard/guidance/${g.id}`} className="kb-btn-primary text-xs" data-testid={`guidance-${g.id}-open-workspace`}>
                          <MessageSquare className="w-3.5 h-3.5" /> Open workspace
                        </Link>
                      )}
                      {(g.status === "submitted" || g.status === "priced") && (
                        <button onClick={() => deleteRequest(g)} disabled={deletingId === g.id}
                          className="kb-mono text-[10px] uppercase tracking-wider text-rose-600 hover:text-rose-700 inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid={`guidance-${g.id}-delete-btn`}>
                          <Trash2 className="w-3 h-3" /> {deletingId === g.id ? "Deleting…" : "Delete"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <Pager {...pager} label="requests" testId="guidance-mine-pager" />
          </div>
        )}
    </div>
  );
}
